import { useState, useEffect } from 'react';
import SoundAvatar from './SoundAvatar.jsx';
import '../../styles/theme.css';

const PHASE_LABELS = {
  lobby: 'In the lobby',
  authoring: 'Writing prompts',
  answering: 'Picking clips',
  bluffing: 'Writing bluffs',
  voting: 'Voting',
  duel: 'Head-to-head',
  results: 'Results',
  reveal: 'The reveal',
};

function labelFor(phase) {
  if (!phase) return '';
  if (PHASE_LABELS[phase]) return PHASE_LABELS[phase];
  if (phase.startsWith('step_')) return `Chain step ${Number(phase.slice(5)) + 1}`;
  return phase.replace(/_/g, ' ');
}

function useCountdown(phaseEndTime) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!phaseEndTime) {
      setRemaining(null);
      return undefined;
    }
    const tick = () => setRemaining(Math.max(0, Math.ceil((phaseEndTime - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [phaseEndTime]);

  return remaining;
}

const PIPS_CONFIG = [
  { h: '16px', speed: '1.40s', delay: '0.00s', center: false },
  { h: '30px', speed: '1.70s', delay: '0.14s', center: false },
  { h: '46px', speed: '2.00s', delay: '0.28s', center: true },
  { h: '30px', speed: '1.40s', delay: '0.42s', center: false },
  { h: '16px', speed: '1.70s', delay: '0.56s', center: false },
];

/**
 * Single waiting surface for all game phases.
 * Matches design/canvas/Waiting.dc.html.
 */
export function PhaseStatus({
  phase,
  progress = null,
  phaseEndTime = null,
  compact = false,
  headline = null,
  subline = null,
  accent = '#3ACFCF',
  room = null,
  players = null,
  isHost = false,
  onForceAdvance = null,
}) {
  const remaining = useCountdown(phaseEndTime);

  if (!phase || phase === 'lobby') return null;

  // Build roster list
  let playerList = [];

  if (Array.isArray(players) && players.length > 0) {
    playerList = players.map((p, idx) => ({
      id: p.id || `p_${idx}`,
      name: p.name || `Player_${idx + 1}`,
      avatar: p.avatar || p.id || p.name || `seed_${idx}`,
      done: p.done !== undefined ? Boolean(p.done) : true,
    }));
  } else if (room?.players) {
    const active = Object.values(room.players).filter((p) => !p.isHost);
    const waitingSet = new Set(
      (progress?.waitingOn || []).map((w) => (typeof w === 'string' ? w : w.id || w.name))
    );
    playerList = active.map((p) => ({
      id: p.id,
      name: p.name,
      avatar: p.avatar || p.id,
      done: !waitingSet.has(p.id) && !waitingSet.has(p.name),
    }));
  } else if (progress) {
    const total = progress.total || 0;
    const waitingOn = progress.waitingOn || [];
    const pendingPlayers = waitingOn.map((w, idx) => ({
      id: typeof w === 'string' ? w : w.id || `pending_${idx}`,
      name: typeof w === 'string' ? w : w.name || `Player_${idx + 1}`,
      avatar: typeof w === 'object' ? w.avatar || w.id || w.name : w,
      done: false,
    }));

    const submittedCount =
      progress.submitted !== undefined
        ? progress.submitted
        : Math.max(0, total - pendingPlayers.length);

    const donePlayers = [];
    for (let i = 0; i < submittedCount; i++) {
      donePlayers.push({
        id: `done_${i}`,
        name: `Player_${i + 1}`,
        avatar: `done_${i}`,
        done: true,
      });
    }
    playerList = [...donePlayers, ...pendingPlayers];
  }

  const doneCount =
    progress?.submitted !== undefined
      ? progress.submitted
      : playerList.filter((p) => p.done).length;
  const totalCount =
    progress?.total !== undefined
      ? progress.total
      : playerList.length;

  const everyoneIn = totalCount > 0 && doneCount >= totalCount;
  const waitingOnList = progress?.waitingOn || playerList.filter((p) => !p.done);
  const waitingNames = waitingOnList.map((w) => (typeof w === 'string' ? w : w.name)).filter(Boolean);

  const defaultHeadline = everyoneIn ? "Everyone’s in." : "Your pair is locked.";

  let defaultSubline;
  if (everyoneIn) {
    defaultSubline = "The host screen is about to play them back, one at a time.";
  } else if (waitingNames.length > 0) {
    if (waitingNames.length <= 3) {
      defaultSubline = `Waiting for ${waitingNames.join(', ')}`;
    } else {
      defaultSubline = `Waiting for ${waitingNames.length} players`;
    }
  } else {
    defaultSubline = "Waiting on the remaining players. Nothing else for you to do yet.";
  }

  const displayHeadline = headline || defaultHeadline;
  const displaySubline = subline || defaultSubline;
  const progressLabel = `${doneCount} of ${totalCount} sent`;
  const urgent = remaining !== null && remaining <= 10;

  return (
    <div
      data-testid="phase-status"
      data-phase={phase}
      data-waiting-count={waitingOnList.length}
      style={{
        width: '100%',
        background: 'var(--sd-ground, #08090B)',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @keyframes sd-drift {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .sd-pip-bar { animation: none !important; }
        }
      `}</style>

      {/* Top Section: Pips & Headlines */}
      <div style={{ flex: 'none', padding: compact ? '20px 16px 16px' : '32px 24px 24px', textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'flex-end',
            gap: '4px',
            height: '46px',
            marginBottom: '16px',
          }}
        >
          {PIPS_CONFIG.map((pip, i) => (
            <div
              key={i}
              className="sd-pip-bar"
              style={{
                width: '6px',
                height: pip.h,
                borderRadius: '3px',
                background: pip.center ? accent : 'rgba(58, 207, 207, 0.32)',
                animation: `sd-drift ${pip.speed} ease-in-out infinite`,
                animationDelay: pip.delay,
              }}
            />
          ))}
        </div>

        <div
          style={{
            fontSize: compact ? '20px' : '24px',
            fontWeight: 800,
            letterSpacing: '-0.024em',
            lineHeight: 1.2,
          }}
        >
          {displayHeadline}
        </div>

        {displaySubline && (
          <div
            data-testid="phase-waiting-text"
            style={{
              marginTop: '8px',
              fontSize: '14.5px',
              lineHeight: 1.5,
              color: everyoneIn ? '#8FC7A0' : '#79838D',
              maxWidth: '32ch',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {displaySubline}
          </div>
        )}

        {isHost && onForceAdvance && (
          <div style={{ marginTop: '16px' }}>
            <button
              type="button"
              data-testid="phase-status-force-advance-btn"
              onClick={onForceAdvance}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                padding: '0 20px',
                borderRadius: '12px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line, #1A1F25)',
                color: 'var(--sd-amber, #E0A458)',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              ⏩ Force Advance Phase
            </button>
          </div>
        )}
      </div>

      {/* Roster & Progress Section */}
      <div
        style={{
          flex: '1 1 0',
          padding: compact ? '0 16px 16px' : '0 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          minHeight: 0,
        }}
      >
        <div
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '6px',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
              fontSize: '10.5px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#6E7982',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: '1 1 0',
            }}
          >
            Room progress ({labelFor(phase)})
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              data-testid="phase-progress-count"
              style={{
                fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                fontSize: '10.5px',
                color: everyoneIn ? '#8FC7A0' : '#79838D',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {progressLabel}
              <span style={{ display: 'none' }}> ({doneCount} / {totalCount})</span>
            </span>

            {remaining !== null && (
              <span
                data-testid="phase-countdown"
                style={{
                  fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: urgent ? '#E4738A' : '#E0A458',
                }}
              >
                {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}
              </span>
            )}
          </div>
        </div>

        {/* Per-Player Roster */}
        {playerList.map((p, idx) => (
          <div
            key={p.id || p.name || idx}
            data-testid="roster-player-item"
            data-player-done={p.done ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
              height: '60px',
              padding: '0 15px',
              borderRadius: '12px',
              border: `1px solid ${p.done ? '#1F252C' : '#171C22'}`,
              background: p.done ? '#0D1015' : 'transparent',
            }}
          >
            <SoundAvatar
              size="sig"
              clip={typeof p.avatar === 'object' ? p.avatar : null}
              seed={typeof p.avatar === 'string' ? p.avatar : p.name}
              style={{
                width: '42px',
                height: '22px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                boxShadow: 'none',
                flex: 'none',
              }}
            />
            <span
              style={{
                flex: '1 1 0',
                fontSize: '14.5px',
                fontWeight: 600,
                color: p.done ? '#EDEFF2' : '#69737C',
              }}
            >
              {p.name}
            </span>
            {p.done ? (
              <div
                data-testid="status-badge-in"
                style={{ display: 'flex', alignItems: 'center', gap: '7px' }}
              >
                <span
                  style={{
                    fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                    fontSize: '10.5px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#8FC7A0',
                  }}
                >
                  In
                </span>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M3.5 8.4l3 3 6-6.8"
                    stroke="#8FC7A0"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            ) : (
              <span
                data-testid="status-badge-pending"
                style={{
                  fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                  fontSize: '10.5px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#79838D',
                }}
              >
                Still picking
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default PhaseStatus;
